import { Avatar, Grid, Skeleton, TextField } from "@mui/material";
import { AppImageDimension, AppImages } from "app/appImages";
import { MprcContainer, MprcStringToHtml } from "components";
import { MprcImageSelection } from "components/MprcImageHandler";
import { FileGetDto, GetEvaluationDto, ManagedMeetingDto } from "generated/CoreApi_v1";
import MediaUtils from "lib/MediaUtils";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditMeetingMode } from "./EditMeeting";

////////////////////////////////////////////////////////////////////////////////

interface IData {
    name: string;
    description: string | null;
    thumbnail: FileGetDto | null;
}
interface IPros {
    mode: EditMeetingMode;
    viewData: IData;
    viewErrors: ApiProblemErrors | undefined;
    nameRef: any;
    readonly?: boolean;
    onFormUpdate: (dto: Partial<ManagedMeetingDto>) => void;
    onFormValidation: (valid: boolean, hasError: boolean) => void;
}

interface IValidationFields {
    name: boolean;
    description: boolean;
}

interface IPostValidation {
    name: string[] | undefined;
    description: string[] | undefined;
}

////////////////////////////////////////////////////////////////////////////////

function getDefaultValidate(dto: IData): IValidationFields {
    var obj = {
        name: dto.name != null && dto.name.length > 0,
        description: dto.description == null || dto.description.length < 513,
    } as IValidationFields;

    return obj;
}

/////////////////////////////////////////////////////////////////////////////////

export const EditMeetingMainBox: React.FC<IPros> = (props) => {

    const { mode, viewData, readonly = false, viewErrors, nameRef, onFormUpdate, onFormValidation, } = props;

    const { t } = useTranslation("lms");
    const [formValidation, setFormValidation] = useState<IValidationFields>(
        getDefaultValidate(viewData)
    );

    const vErrors = viewErrors ?? {};
    const postValidation: IPostValidation = {
        name: vErrors["name"],
        description: vErrors["description"],
    };

    /////////////////////////////////////////////////////////////////////////////////

    ///
    const handleUpdateRecord = (name: keyof GetEvaluationDto, v: any) => {
        var newViewData = { ...viewData, [name]: v };
        onFormUpdate(newViewData);
        setFormValidation(getDefaultValidate(newViewData));
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECTS
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        onFormValidation(
            Object.values(formValidation).filter((x) => x === false).length == 0,
            Object.values(postValidation).filter(x => x !== undefined).length > 0
        );
    }, [formValidation]);

    useEffect(() => {

    }, []);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <MprcContainer>
            <Grid container spacing={2}>
                <Grid alignSelf="flex-start" justifySelf="center">
                    {mode === "training" ?
                        <MprcImageSelection
                            variant="rounded"
                            allowEdit={!readonly}
                            allowSelect={!readonly}
                            initialSource="local"
                            backgroundType="solid"
                            emptyImageUrl={AppImages.LmsContentTypeLivePresentialThumbnail}
                            currentImageUrl={viewData.thumbnail?.url}
                            acceptedFiles={MediaUtils.DropzoneAcceptImages()}
                            imageDimensions={AppImageDimension.ThumbnailWide}
                            onClear={() => {
                                handleUpdateRecord("thumbnail", null);
                            }}
                            onFileUploadSuccess={(data) => {
                                handleUpdateRecord("thumbnail", data.thumbnail ? data.thumbnail : data.mainFile);
                                return true;
                            }}
                            onFileUploadStarted={() => setFormValidation((previousState) => ({ ...previousState, thumbnail: [""] }))}
                            onFileUploadFinished={() => setFormValidation((previousState) => ({ ...previousState, thumbnail: null }))}
                            selectButtomText={t("Meeting.MainBox.SelectImageButtonText")}
                        />
                        :
                        <Avatar
                            variant="rounded"
                            sx={{ width: AppImageDimension.ThumbnailWide.width, height: AppImageDimension.ThumbnailWide.height }}
                            src={viewData.thumbnail?.url ?? AppImages.LmsContentTypeLivePresentialThumbnail}
                        />
                    }
                </Grid>
                <Grid size="grow">
                    <MprcContainer>
                        <TextField
                            fullWidth
                            required
                            name="name"
                            autoComplete="off"
                            label={t("Meeting.MainBox.NameLabel")}
                            margin="dense"
                            autoFocus
                            inputRef={nameRef}
                            slotProps={{
                                input: {
                                    inputProps: { maxLength: 255 }
                                }
                            }}
                            value={viewData?.name || ""}
                            onChange={(e) => handleUpdateRecord("name", e.target.value)}
                            error={!formValidation.name || !!postValidation.name}
                            helperText={postValidation?.name && <MprcStringToHtml text={postValidation?.name} />}
                            disabled={mode === "classroom" || readonly}
                        />
                        <TextField
                            fullWidth
                            name="description"
                            autoComplete="off"
                            disabled={readonly}
                            label={t("Meeting.MainBox.DescriptionLabel")}
                            margin="dense"
                            multiline
                            maxRows={4}
                            slotProps={{
                                input: {
                                    inputProps: { maxLength: 512 }
                                }
                            }}
                            value={viewData?.description || ""}
                            onChange={(e) => handleUpdateRecord("description", e.target.value)}
                            error={viewData?.description != undefined && !!postValidation.description}
                            helperText={postValidation?.description && <MprcStringToHtml text={postValidation?.description} />}
                        />
                    </MprcContainer>
                </Grid>
            </Grid>
        </MprcContainer>
    );
};

export const EditMeeetingMainBoxSkeleton = () => {
    return (
        <MprcContainer>
            <Grid container spacing={2}>
                <Grid alignSelf="flex-start" justifySelf="center">
                    <Skeleton variant="rectangular" width={AppImageDimension.ThumbnailWide.width} height={AppImageDimension.ThumbnailWide.height} />
                </Grid>
                <Grid size="grow">
                    <MprcContainer>
                        <Skeleton variant="rectangular" width="100%" height={32} />
                        <br />
                        <Skeleton variant="rectangular" width="100%" height={64} />
                    </MprcContainer>
                </Grid>
            </Grid>
        </MprcContainer>
    );
};